import {
  RoadTestTaskDetail,
  RoadTestTaskType,
  RoadTestResult,
  ItemResultDTO,
  RoadTestScheme,
  VehicleOwnerUseCase,
} from '@/fetch/bussiness/roadtest/types';

export { VehicleOwnerUseCase };

/** 路测抽屉模式 */
export enum RoadTestMode {
  CREATE = 'create',
  EVALUATE = 'evaluate',
}

export const ROAD_TEST_OWNER_USE_CASE = VehicleOwnerUseCase.PRODUCT;

const ROAD_TEST_TASK_TYPE_NAME: Record<RoadTestTaskType, string> = {
  [RoadTestTaskType.COMPREHENSIVE]: '综合',
  [RoadTestTaskType.FATIGUE]: '疲劳',
};

/**
 * 是否展示路测入口
 */
export const isRoadTestEnabled = (ownerUseCase?: string | null): boolean => {
  return ownerUseCase === ROAD_TEST_OWNER_USE_CASE;
};

/**
 * 当前是否有路测任务进行中
 * 依据 /monitor/web/basic/vehicle 返回的 roadTaskExist 字段判断
 */
export const isTaskInProgress = (roadTaskExist?: boolean | null): boolean => {
  return !!roadTaskExist;
};

/**
 * 将路测方案列表格式化为 Select options
 */
export const formatSchemeOptions = (schemes: RoadTestScheme[] = []) => {
  return schemes.map((scheme) => ({
    label: scheme.schemeName,
    value: scheme.schemeNo,
  }));
};

/**
 * 整体测试结果切换类型
 *
 */
export enum OverallResultChangeType {
  EMPTY_TO_PASS = 'EMPTY_TO_PASS',
  EMPTY_TO_FAIL = 'EMPTY_TO_FAIL',
  PASS_TO_FAIL = 'PASS_TO_FAIL',
  FAIL_TO_PASS = 'FAIL_TO_PASS',
}

/**
 * 判断整体结果切换类型
 */
export const getOverallResultChangeType = (
  prev: RoadTestResult | null | undefined,
  next: RoadTestResult | null | undefined
): OverallResultChangeType | null => {
  if (prev === next) return null;
  if (!prev && next === RoadTestResult.PASS)
    return OverallResultChangeType.EMPTY_TO_PASS;
  if (!prev && next === RoadTestResult.FAIL)
    return OverallResultChangeType.EMPTY_TO_FAIL;
  if (prev === RoadTestResult.PASS && next === RoadTestResult.FAIL)
    return OverallResultChangeType.PASS_TO_FAIL;
  if (prev === RoadTestResult.FAIL && next === RoadTestResult.PASS)
    return OverallResultChangeType.FAIL_TO_PASS;
  return null;
};

/**
 * 是否需要填写修改原因
 */
export const isChangeReasonRequired = (
  prev: RoadTestResult | null | undefined,
  next: RoadTestResult | null | undefined
): boolean => {
  const changeType = getOverallResultChangeType(prev, next);
  return (
    changeType !== OverallResultChangeType.EMPTY_TO_PASS && changeType !== null
  );
};

/**
 * 测试项下全部题目是否通过
 */
/**
 * 测试项下所有题目是否都已回答（不存在 null）
 */
export const isAllQuestionsPassed = (
  questionList: { result?: boolean | null }[]
): boolean => {
  if (!questionList || questionList.length === 0) return false;
  return questionList.every((q) => q.result !== null);
};

/**
 * 本地缓存数据结构
 */
export type LocalAnswers = Record<string, ItemResultDTO>;

/**
 * 根据服务端任务详情构建本地答案缓存
 */
export const buildLocalAnswers = (
  remoteData: RoadTestTaskDetail
): LocalAnswers => {
  const result: LocalAnswers = {};
  (remoteData.itemList || []).forEach((item) => {
    result[item.itemNo] = {
      itemNo: item.itemNo,
      questionList: (item.questionList || []).map((q) => ({
        questionNo: q.questionNo,
        result: q.passed ?? null,
      })),
    };
  });
  return result;
};

/**
 * 将本地缓存转换为接口要求的 itemResult 数组
 */
export const localAnswersToItemResult = (
  localAnswers: LocalAnswers
): ItemResultDTO[] => {
  return Object.values(localAnswers).map((item) => ({
    itemNo: item.itemNo,
    questionList: item.questionList.map((q) => ({
      questionNo: q.questionNo,
      result: q.result ?? null,
    })),
  }));
};

/**
 * 获取未填写完整的测试项名称列表
 * 只要有任意题目 result 为 null/undefined，即视为该测试项未填写完整
 */
export const getIncompleteItemNames = (
  itemList: RoadTestItemDetail[],
  localAnswers: LocalAnswers
): string[] => {
  return itemList
    .filter((item) => {
      const answered = localAnswers[item.itemNo]?.questionList || [];
      return answered.some((q) => q.result === null || q.result === undefined);
    })
    .map((item) => item.itemName || item.itemNo);
};

/**
 * 获取题目当前结果（优先从本地缓存取）
 */
export const getQuestionResult = (
  localAnswers: LocalAnswers,
  itemNo: string,
  questionNo: string
): boolean | null => {
  const item = localAnswers[itemNo];
  if (!item) return null;
  const question = item.questionList.find((q) => q.questionNo === questionNo);
  return question?.result ?? null;
};

/**
 * 更新题目结果到本地缓存
 */
export const setQuestionResult = (
  localAnswers: LocalAnswers,
  itemNo: string,
  questionNo: string,
  result: boolean | null
): LocalAnswers => {
  const item = localAnswers[itemNo];
  if (!item) return localAnswers;
  return {
    ...localAnswers,
    [itemNo]: {
      ...item,
      questionList: item.questionList.map((q) =>
        q.questionNo === questionNo ? { ...q, result } : q
      ),
    },
  };
};

/**
 * 用服务端新数据合并到本地缓存
 * 规则：本地 result === null 时才覆盖（接受服务端值）
 *       本地 result !== null 时保留本地值（用户已编辑或系统已赋值）
 * 优化：若所有题目结果均未变化，返回原 localAnswers 引用，避免下游 memo 失效
 */
export const mergeRemoteDataToLocalAnswers = (
  localAnswers: LocalAnswers,
  remoteData: RoadTestTaskDetail
): LocalAnswers => {
  let changed = false;
  const next: LocalAnswers = { ...localAnswers };
  (remoteData.itemList || []).forEach((item) => {
    const existingItem = next[item.itemNo];
    const existingQuestions = existingItem?.questionList;

    // 本地没有该测试项，用服务端数据初始化
    if (!existingQuestions) {
      changed = true;
      next[item.itemNo] = {
        itemNo: item.itemNo,
        questionList: (item.questionList || []).map((q) => ({
          questionNo: q.questionNo,
          result: q.passed ?? null,
        })),
      };
      return;
    }

    // 构建新的题目列表：仅当本地 result === null 时才用服务端值覆盖
    const mergedQuestions = existingQuestions.map((q) => {
      // 本地有值（非 null），不覆盖
      if (q.result !== null) return q;
      
      // 本地没值（null），找服务端的值
      const remoteQuestion = (item.questionList || []).find(
        (rq) => rq.questionNo === q.questionNo
      );
      const remoteResult = remoteQuestion?.passed ?? null;
      
      // 服务端有值，覆盖
      if (remoteResult !== null && remoteResult !== q.result) {
        changed = true;
        return { ...q, result: remoteResult };
      }
      return q;
    });

    if (mergedQuestions !== existingQuestions) {
      changed = true;
      next[item.itemNo] = {
        itemNo: item.itemNo,
        questionList: mergedQuestions,
      };
    }
  });
  return changed ? next : localAnswers;
};

/**
 * 获取任务类型显示文本
 */
export const getTaskTypeName = (taskType?: RoadTestTaskType | null): string => {
  if (!taskType) return '';
  return ROAD_TEST_TASK_TYPE_NAME[taskType] ?? '';
};
